import React, { useState } from "react";
import { FluentProvider, webDarkTheme } from "@fluentui/react-components";
import { ArrowRight24Regular } from "@fluentui/react-icons";
import "./Agentic.scss";
import { concepts } from "./Data/concepts";
import ConceptPreview from "./Concepts/ConceptPreview";
import agenimage from "../../../../assets/Images/portrait-young-handsome-man.jpg";
const industries = [
  {
    id: "hotel",
    title: "Hotel Group",
    description: "Agentic AI that orchestrates guest journeys, dynamic pricing, and operational staff across properties.",
    tag: "Hospitality",
  },
  {
    id: "ecommerce",
    title: "E-commerce Brand",
    description: "Autonomous agents for catalog enrichment, personalized merchandising, and post-purchase support at scale.",
    tag: "Retail",
  },
  {
    id: "fintech",
    title: "Bank / Fintech",
    description: "Secure, governed agents for fraud detection, credit decisioning, and real-time customer support.",
    tag: "Financial Services",
  },
  {
    id: "airline",
    title: "Airline / Travel",
    description: "Agents that rebook disrupted itineraries, optimize crew and aircraft, and personalize the traveler experience.",
    tag: "Travel",
  },
  {
    id: "logistics",
    title: "Logistics / Delivery",
    description: "Production agents that plan routes, resolve exceptions, and coordinate multi-party handoffs in real time.",
    tag: "Supply Chain",
  },
  {
    id: "hospital",
    title: "Hospital / Clinic",
    description: "Clinical and operational agents that reduce administrative load while keeping humans firmly in the loop.",
    tag: "Healthcare",
  },
];

const services = [
  {
    id: "kiro",
    badge: "IDE",
    title: "Kiro",
    description: "Bring engineering rigor to AI-native coding at scale. Spec-driven development that produces production-ready software.",
  },
  {
    id: "security",
    badge: "Agent",
    title: "Security Agent",
    description: "Build applications that are secure from the start with continuous, context-aware security testing.",
  },
  {
    id: "devops",
    badge: "Agent",
    title: "DevOps Agent",
    description: "Resolve and proactively prevent incidents across your entire stack, autonomously.",
  },
  {
    id: "finops",
    badge: "Service",
    title: "FinOps Agent",
    description: "Monitor costs, investigate anomalies, and optimize spend — turning financial decisions from reactive to proactive.",
  },
  {
    id: "connect",
    badge: "Service",
    title: "Connect",
    description: "Transform customer service, hiring, and supply-chain functions with agentic AI built on real operational expertise.",
  },
  {
    id: "agentcore",
    badge: "Platform",
    title: "AgentCore",
    description: "Build, deploy, and operate production-ready agents with enterprise-grade governance and security at scale.",
  },
];

// Put this near the top (replace the old services array)
const tabContent: Record<string, { id: string; badge: string; title: string; description: string }[]> = {
  applications: [
    {
      id: "crm",
      badge: "CRM",
      title: "Cilpron CRM Agent",
      description: "Autonomous agents that manage leads, opportunities, and customer journeys across sales and service teams.",
    },
    {
      id: "hcm",
      badge: "HCM",
      title: "Cilpron HCM Agent",
      description: "Agents that handle recruitment, onboarding, leave, and employee self-service with full auditability.",
    },
    {
      id: "chat",
      badge: "Chat",
      title: "Cilpron Chat Agent",
      description: "Conversational agents that resolve customer and employee queries across channels in real time.",
    },
    {
      id: "hospitality",
      badge: "Hospitality",
      title: "Cilpron Hospitality Agent",
      description: "Purpose-built agents for hotel groups — guest journey, dynamic pricing, and property operations.",
    },
    {
      id: "insurance",
      badge: "Insurance",
      title: "Cilpron Insurance Agent",
      description: "Agents that underwrite, process claims, and support policyholders with governed decisioning.",
    },
    {
      id: "hmo",
      badge: "HMO",
      title: "Cilpron HMO Agent",
      description: "Clinical and administrative agents for health maintenance organisations and care networks.",
    },
  ],
  frontier: [
    {
      id: "fo",
      badge: "F&O",
      title: "Cilpron Finance & Operations",
      description: "Frontier agents that close books, manage supply chain, and surface financial insights autonomously.",
    },
    {
      id: "accounting",
      badge: "Accounting",
      title: "Cilpron Accounting Agent",
      description: "Agents that reconcile accounts, generate reports, and flag anomalies before month-end.",
    },
  ],
  tools: [
    {
      id: "email",
      badge: "Email",
      title: "Cilpron Email & Calendar Agent",
      description: "Agents that read, prioritise, draft, and schedule across SMTP, calendars, and collaboration tools.",
    },
    {
      id: "builder",
      badge: "Builder",
      title: "Cilpron Agent Builder",
      description: "Low-code tools to design, test, and deploy custom agents on top of your existing systems.",
    },
  ],
  models: [
    {
      id: "models",
      badge: "Models",
      title: "Cilpron Domain Models",
      description: "Specialised models fine-tuned for hospitality, insurance, healthcare, and enterprise operations.",
    },
  ],
  infra: [
    {
      id: "infra",
      badge: "Platform",
      title: "Cilpron Agentic Infrastructure",
      description: "Secure orchestration, memory, governance, and observability so agents run reliably at production scale.",
    },
  ],
};

const Agentic: React.FC = () => {
  const [selectedConcept] = useState(concepts[0]);
  const [activeTab, setActiveTab] = useState("applications");

  return (
    <FluentProvider theme={webDarkTheme}>
      <div className="agentic-page">

      {/* ===================== HERO ===================== */}
    {/* ===================== HERO ===================== */}
<section className="agentic-hero-page">
  {/* Full background image */}
  <div className="agentic-hero-bg">
    <img
      src={agenimage}
      alt=""
      className="agentic-hero-bg-img"
    />
  </div>

  {/* Dark overlay so text is readable */}
  <div className="agentic-hero-overlay" />

  <div className="agentic-hero-container">
    <nav className="agentic-hero-breadcrumb">
      <a href="#">Products</a>
      <span>›</span>
      <a href="#">Artificial Intelligence</a>
      <span>›</span>
      <span className="agentic-hero-current">Agentic AI</span>
    </nav>

    <div className="agentic-hero-card">
      <h1 className="agentic-hero-title">
        Agentic AI that moves as<br />
        fast as you do
      </h1>
      <p className="agentic-hero-desc">
        Cilpron introduces new capabilities across work, development, and agent infrastructure, removing the constraints between idea and production. See the full announcement.
      </p>
      <div className="agentic-hero-ctas">
        <a href="auth/signup" className="agentic-hero-btn-primary">
          Book a Demo
        </a>
        <a href="#" className="agentic-hero-btn-secondary">
          Contact us
        </a>
      </div>
    </div>
  </div>
  <div className="Agentic-cl-hero__curve"></div>
</section>



        {/* ===================== SOLUTIONS ===================== */}
      {/* ===================== SOLUTIONS ===================== */}
<section className="agentic-section">
  <div className="agentic-container">
    <h2 className="agentic-section-title">
      Cilpron Agentic solutions that remove constraints across your business
    </h2>
    <p className="agentic-section-lead">
      Build differently, deploy agents you can trust, and put AI to work the way your teams already operate.
    </p>

    <div className="agentic-card-grid agentic-three">
      <article className="agentic-feature-card">
        <div className="agentic-card-visual agentic-v1" />
        <h3>Transform how your teams build and ship</h3>
        <p>
          Cilpron’s agentic platform helps your engineers and operations teams move from experimentation 
          to production-ready agents without rewriting their workflows.
        </p>
      </article>

      <article className="agentic-feature-card">
        <div className="agentic-card-visual agentic-v2" />
        <h3>Deploy production-ready agents with enterprise trust</h3>
        <p>
          Built-in orchestration, memory, governance, and security so your agents work reliably 
          at scale across hotel groups, banks, airlines, logistics, and healthcare.
        </p>
      </article>

      <article className="agentic-feature-card">
        <div className="agentic-card-visual agentic-v3" />
        <h3>The AI teammate that changes how work gets done</h3>
        <p>
          Cilpron agents connect to your existing systems, learn your processes, 
          and take action on your behalf — with full visibility and control.
        </p>
      </article>
    </div>
  </div>
</section>
        {/* ===================== INDUSTRIES ===================== */}
        <section className="agentic-section">
          <div className="agentic-container">
            <h2 className="agentic-section-title">How customers are creating value with agentic AI</h2>
            <div className="agentic-industry-grid">
              {industries.map((item) => (
                <article key={item.id} className="agentic-industry-card">
                  <span className="agentic-tag">{item.tag}</span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <button className="agentic-ghost-btn">
                    Explore <ArrowRight24Regular />
                  </button>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== SERVICES ===================== */}
   
<section className="agentic-section">
  <div className="agentic-container">
    <h2 className="agentic-section-title">
      Put Cilpron Agentic AI to work for your business today
    </h2>

    <div className="agentic-tabs">
      {[
        { id: "applications", label: "Agent-powered applications" },
        { id: "frontier", label: "Frontier agents" },
        { id: "tools", label: "Tools to build AI agents" },
        { id: "models", label: "Models" },
        { id: "infra", label: "Agentic Infrastructure" },
      ].map((tab) => (
        <button
          key={tab.id}
          className={`agentic-tab ${activeTab === tab.id ? "agentic-tab-active" : ""}`}
          onClick={() => setActiveTab(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>

    <div className="agentic-service-grid">
      {tabContent[activeTab]?.map((svc) => (
        <article key={svc.id} className="agentic-service-card">
          <span className="agentic-badge">{svc.badge}</span>
          <h3>{svc.title}</h3>
          <p>{svc.description}</p>
          <a href="#" className="agentic-card-link">
            Learn more <ArrowRight24Regular />
          </a>
        </article>
      ))}
    </div>
  </div>
</section>
        {/* ===================== CTA ===================== */}
        <section className="agentic-cta-banner">
          <div className="agentic-container agentic-banner-inner">
            <div>
              <h3>Your agent demo took a week. Now what?</h3>
              <p>Get agents out of pilot and into production. Learn from leading companies already running agents at scale.</p>
            </div>
            <button className="agentic-primary-btn">Close the production gap</button>
          </div>
        </section>

        {/* ===================== PREVIEW ===================== */}
        <section className="agentic-section agentic-preview-section">
          <div className="agentic-container">
            <h2 className="agentic-section-title">Explore agentic experiences live</h2>
            <div className="agentic-preview-shell">
              <ConceptPreview
                concept={selectedConcept}
                simulationParams={{
                  typingSpeed: 2500,
                  showPlatformBadges: true,
                  simulatedTraffic: 140,
                }}
              />
            </div>
          </div>
        </section>

      </div>
    </FluentProvider>
  );
};

export default Agentic;